import { Box, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import React from 'react';
import { spaces } from '../../../../assets/styles/themes/tokens';
import getTextTruncationSx from '../../../../utils/getTextTruncationSx';
import SortByFilterNewUI from '../../../SearchFilters/SortByFilterNewUI';
import { searchSettings } from '../../../../constants';
import { useNewDesign } from '../../../../state/DesignPreferenceContextProvider';
import useQueryModification from '../../../../routes/SearchPage/useQueryModification';
import { SearchFilters } from '../../../../types/search';
import useRouter from '../../../../utils/useRouter';
import useScreenSize from '../../../../utils/useScreenSize';

export type SearchResultsTopbarNewUIProps = {
  total: number;
  searchTerm?: string;
  requestFilters: SearchFilters;
  isLoaded: boolean;
  withSortByFilter?: boolean;
};

const SearchResultsTopbarNewUI: React.FC<SearchResultsTopbarNewUIProps> = ({
  total,
  searchTerm,
  requestFilters,
  isLoaded = false,
  withSortByFilter = true,
}) => {
  const { t } = useTranslation();
  const { isSmallScreen } = useScreenSize();
  const searchTermText = searchTerm ? `“${searchTerm}”` : t('all results');
  const totalText = total > 0 ? `${total} ` : null;
  const { prefersNewDesign } = useNewDesign();
  const { router, match } = useRouter();
  const { updateQueryParams } = useQueryModification<SearchFilters>(
    router,
    match
  );

  if (!isLoaded) return null;

  return (
    <Box
      data-testid='search-results-topbar'
      sx={{
        display: 'inline-flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        marginY: spaces.md,
        color: 'text.primary',
        ...(isSmallScreen
          ? {
            flexDirection: 'column',
          }
          : {}),
      }}
    >
      <Box
        data-testid='search-results-topbar__total'
        sx={{
          ...(isSmallScreen ? { marginRight: 'auto' } : {}),
        }}
      >
        <Typography
          data-testid='search-results-header-new-ui'
          variant='subtitle1'
          sx={{
            maxWidth: isSmallScreen ? '92vw' : '58vw', // We need that line to crop the long search term
            color: 'text.primary',
            whiteSpace: 'nowrap',
            ...getTextTruncationSx(),
          }}
        >
          <Trans
            i18nKey={'<totalText> search results for "<searchTermText>"'}
            values={{ totalText, searchTermText }}
          >
            <Box
              data-testid='search-results-header-new-ui__query'
              component='span'
              sx={{ fontWeight: '600' }}
            >
              {searchTermText}
            </Box>
          </Trans>
        </Typography>
      </Box>
      {withSortByFilter && (!isSmallScreen || !prefersNewDesign) && (
        <Box
          data-testid='sort-by-new-ui-filter'
          sx={{
            ...(isSmallScreen ? { marginLeft: 'auto' } : {}),
          }}
        >
          <SortByFilterNewUI
            currentValue={requestFilters.sortBy}
            updateFilter={(sortBy) => updateQueryParams({ sortBy })}
            clearFilter={() =>
              updateQueryParams({ sortBy: searchSettings.defaultSortBy })
            }
          />
        </Box>
      )}
    </Box>
  );
};

export default SearchResultsTopbarNewUI;
